import React from 'react';
import { Icon } from '@/shared/icons';
import { Button } from '@/shared/components/ui/Button';
import type { HistoryImportRun, HistoryImportRunStatus } from '@troia-v3/types';

const FAILURE_STATUSES: ReadonlySet<HistoryImportRunStatus> = new Set(['failed', 'aborted', 'expired']);

/** Motivos conhecidos de `run.error` (ver `HistoryImportsService.failRun` callers) — fallback mostra a string crua. */
const FAILURE_REASON_LABELS: Record<string, string> = {
  disconnected: 'A sessão do WhatsApp foi desconectada antes de concluir a captura.',
  timeout: 'O tempo limite desta etapa foi atingido.',
};

/**
 * Espelha `computePercentComplete` do backend (`history-imports/service.ts`)
 * — usado como fallback quando ainda não chegou nenhum evento de socket com
 * `percentComplete` (ex.: reconexão do socket, ou logo na entrada do passo).
 */
function computeLocalPercent(run: HistoryImportRun): number {
  if (run.status === 'completed') return 100;
  if (run.status === 'importing_media' && run.counters.mediaTotal > 0) {
    return Math.round(((run.counters.mediaDone + run.counters.mediaFailed) / run.counters.mediaTotal) * 100);
  }
  return 0;
}

function statusLabel(status: HistoryImportRunStatus): string {
  switch (status) {
    case 'ingesting':
      return 'Importando mensagens...';
    case 'importing_media':
      return 'Baixando mídias...';
    case 'completed':
      return 'Importação concluída';
    default:
      return 'Processando...';
  }
}

const SummaryStat: React.FC<{ label: string; value: number; testId: string }> = ({ label, value, testId }) => (
  <div className="rounded-lg border p-3 text-center" style={{ borderColor: '#e5e7eb' }}>
    <p className="text-lg font-semibold" style={{ color: '#1e293b' }} data-testid={testId}>
      {value}
    </p>
    <p className="text-xs" style={{ color: '#6b7280' }}>
      {label}
    </p>
  </div>
);

export interface ProgressStepProps {
  run: HistoryImportRun;
  /** Último `percentComplete` recebido via socket — `undefined` até o primeiro evento. */
  livePercent: number | undefined;
  /** Fecha o modal (chamado em "Concluir"/"Fechar"). */
  onFinish: () => void;
}

/**
 * Passo 4 do wizard — barra de progresso (`ingesting`/`importing_media`),
 * resumo final por counters (`completed`) ou estado de erro claro
 * (`failed`/`aborted`/`expired`).
 */
export const ProgressStep: React.FC<ProgressStepProps> = ({ run, livePercent, onFinish }) => {
  if (FAILURE_STATUSES.has(run.status)) {
    const reasonLabel = run.error
      ? (FAILURE_REASON_LABELS[run.error] ?? run.error)
      : 'Motivo não informado.';
    const title =
      run.status === 'aborted'
        ? 'Importação cancelada'
        : run.status === 'expired'
          ? 'Importação expirou'
          : 'Falha na importação';

    return (
      <div className="text-center space-y-4 py-6" data-testid="import-failed-state">
        <Icon name="x-circle" className="w-10 h-10 mx-auto" style={{ color: '#ef4444' }} />
        <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
          {title}
        </p>
        <p className="text-xs" style={{ color: '#6b7280' }} data-testid="import-failure-reason">
          {reasonLabel}
        </p>
        <Button variant="secondary" onClick={onFinish} data-testid="import-close-button">
          Fechar
        </Button>
      </div>
    );
  }

  const percent = livePercent ?? computeLocalPercent(run);
  const isDone = run.status === 'completed';

  return (
    <div className="space-y-4 py-2" data-testid="import-progress-state">
      <div className="flex items-center justify-center gap-2">
        {!isDone && (
          <Icon name="loader" className="w-4 h-4 animate-spin" style={{ color: '#8b5cf6' }} />
        )}
        {isDone && <Icon name="check-circle" className="w-5 h-5" style={{ color: '#16a34a' }} />}
        <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
          {statusLabel(run.status)}
        </p>
      </div>

      <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb' }}>
        <div
          className="h-full transition-all duration-300"
          data-testid="import-progress-bar"
          style={{ width: `${percent}%`, backgroundColor: '#8b5cf6' }}
        />
      </div>
      <p className="text-xs text-center" style={{ color: '#6b7280' }}>
        {percent}%
      </p>

      <div className="grid grid-cols-2 gap-3" data-testid="import-summary">
        <SummaryStat
          label="Mensagens inseridas"
          value={run.counters.messagesInserted}
          testId="import-summary-inserted"
        />
        <SummaryStat
          label="Duplicadas (ignoradas)"
          value={run.counters.messagesDuplicated}
          testId="import-summary-duplicated"
        />
        <SummaryStat
          label="Mídias processadas"
          value={run.counters.mediaDone}
          testId="import-summary-media-done"
        />
        <SummaryStat
          label="Mídias com falha"
          value={run.counters.mediaFailed}
          testId="import-summary-media-failed"
        />
      </div>

      {isDone && (
        <Button
          variant="primary"
          fullWidth
          onClick={onFinish}
          data-testid="import-finish-button"
        >
          Concluir
        </Button>
      )}
    </div>
  );
};

ProgressStep.displayName = 'ProgressStep';
